import { authenticator } from 'otplib';
import QRCode from 'qrcode';

// Configure TOTP with browser-compatible settings
authenticator.options = {
  window: 1, // ±30 seconds window
  step: 30,
  digits: 6,
  algorithm: 'SHA1'
};

// Generate a base32 secret
export const generateSecret = () => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  const length = 16; // 16 characters = 80 bits of entropy
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  
  return Array.from(array)
    .map(byte => charset[byte % charset.length])
    .join('');
};

// Generate TOTP URI for QR code
export const generateTOTPUri = (email: string, secret: string) => {
  return `otpauth://totp/K-Prod:${encodeURIComponent(email)}?secret=${secret}&issuer=K-Prod&algorithm=SHA1&digits=6&period=30`;
};

// Generate QR code as data URL
export const generateQRCode = async (uri: string): Promise<string> => {
  try {
    return await QRCode.toDataURL(uri, {
      errorCorrectionLevel: 'H',
      margin: 1,
      width: 300,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
  } catch (err) {
    console.error('Error generating QR code:', err);
    throw err;
  }
};

// Convert base32 to Uint8Array
function base32ToBytes(base32: string): Uint8Array {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let bits = 0;
  let value = 0;
  let index = 0;
  const output = new Uint8Array(Math.ceil(base32.length * 5 / 8));

  for (let i = 0; i < base32.length; i++) {
    value = (value << 5) | alphabet.indexOf(base32[i]);
    bits += 5;

    if (bits >= 8) {
      output[index++] = (value >>> (bits - 8)) & 255;
      bits -= 8;
    }
  }

  return output;
}

// Convert number to Uint8Array
function numberToBytes(num: number): Uint8Array {
  const bytes = new Uint8Array(8);
  for (let i = 7; i >= 0; i--) {
    bytes[i] = num & 0xff;
    num = num >> 8;
  }
  return bytes;
}

// Generate TOTP token
async function generateToken(secret: string, counter: number): Promise<string> {
  try {
    // Convert secret from base32 to bytes
    const keyBytes = base32ToBytes(secret.toUpperCase());
    
    // Create CryptoKey
    const key = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'HMAC', hash: 'SHA-1' },
      false,
      ['sign']
    );

    // Create counter bytes
    const counterBytes = numberToBytes(counter);

    // Generate HMAC
    const signature = await crypto.subtle.sign(
      'HMAC',
      key,
      counterBytes
    );

    // Get the byte array
    const hmac = new Uint8Array(signature);

    // Get offset
    const offset = hmac[hmac.length - 1] & 0xf;

    // Generate 4-byte code
    const code = 
      ((hmac[offset] & 0x7f) << 24) |
      ((hmac[offset + 1] & 0xff) << 16) |
      ((hmac[offset + 2] & 0xff) << 8) |
      (hmac[offset + 3] & 0xff);

    // Generate 6-digit code
    const token = (code % 1000000).toString().padStart(6, '0');

    return token;
  } catch (err) {
    console.error('Error generating token:', err);
    throw err;
  }
}

// Verify TOTP token
export const verifyTOTP = async (token: string, secret: string): Promise<boolean> => {
  try {
    // Clean the token
    const cleanToken = token.replace(/\s/g, '');
    
    // Basic validation
    if (cleanToken.length !== 6) {
      console.log('Invalid token length:', cleanToken.length);
      return false;
    }

    if (!/^\d+$/.test(cleanToken)) {
      console.log('Token contains non-numeric characters');
      return false;
    }

    if (!secret || !/^[A-Z2-7]+$/.test(secret)) {
      console.log('Invalid secret format');
      return false;
    }

    // Get current time step
    const timeStep = Math.floor(Date.now() / 30000);

    // Generate tokens for current and adjacent time steps
    const tokens = await Promise.all([
      generateToken(secret, timeStep - 1),
      generateToken(secret, timeStep),
      generateToken(secret, timeStep + 1)
    ]);

    // Compare with provided token
    return tokens.includes(cleanToken);

  } catch (err) {
    console.error('Error verifying TOTP:', err);
    return false;
  }
};